import type { CaseStudy, ConsultingArea, Product } from './types'

export const consultingAreas: ConsultingArea[] = [
  {
    name: 'Alta Gestão',
    focus: 'Planejamento · indicadores · tomada de decisão · metas · alçadas',
    text: 'Quando a empresa cresce, uma decisão mal pensada pode custar caríssimo. Método, informação e responsabilidades claras passam a ser obrigatórios. Entregamos ao board um formato de trabalho que permite priorizar o que realmente importa — estratégia, crescimento e vendas — enquanto cada líder responde pela sua área com segurança e respaldo.',
    media: {
      desktopSrc: '/assets/consulting/alta-gestao.webp',
      alt: 'Estruturas e lideranças representadas em uma composição tecnológica',
      desktopPosition: '68% center',
      mobilePosition: '68% center',
      mobileScale: 1.1,
    },
    see: { label: 'NORTE — Indicadores, Gestão e Decisão', href: 'https://norte.geniuslead.com.br' },
  },
  {
    name: 'Tesouraria',
    focus: 'Fluxo de caixa · contas a pagar · contas a receber · bancos · crédito · previsibilidade',
    text: 'Não basta olhar o saldo do banco hoje. É preciso saber o que vai entrar, quando vai entrar e o que ainda vai sair. A decisão certa precisa ser tomada antes de o problema chegar ao caixa. Estruturamos a rotina financeira para dar previsibilidade, controle e melhores condições de decisão.',
    media: {
      desktopSrc: '/assets/consulting/tesouraria.webp',
      alt: 'Fluxos financeiros conectando estruturas digitais de controle',
      desktopPosition: '80% center',
      mobilePosition: '76% center',
      mobileScale: 1.1,
    },
    see: { label: 'OESTE — Despesas, aprovações e controle financeiro', href: 'https://oeste.geniuslead.com.br' },
  },
  {
    name: 'Controladoria',
    focus: 'DRE · balanço · plano de contas · centros de custos · margem · classificação de despesas',
    text: 'Faturamento maior nem sempre significa mais lucro. É preciso acompanhar margem, custos, despesas e resultado. Organizamos as informações para mostrar com clareza onde a empresa ganha, onde perde e onde precisa agir.',
    media: {
      desktopSrc: '/assets/consulting/controladoria.webp',
      alt: 'Dados financeiros transformados em indicadores de gestão',
      desktopPosition: '76% center',
      mobilePosition: '72% center',
      mobileScale: 1.1,
    },
    see: { label: 'OESTE — Despesas, aprovações e controle financeiro', href: 'https://oeste.geniuslead.com.br' },
  },
  {
    name: 'Pessoas & Cultura',
    focus: 'Desenvolvimento · liderança · identidade · coesão · retenção',
    text: 'Empresa cresce quando as pessoas crescem junto. Ajudamos as lideranças da sua empresa a se desenvolver, assumir responsabilidades e ganhar autonomia. Fortalecemos a cultura para que boas pessoas assumam mais, trabalhem melhor juntas e tenham motivo para permanecer.',
    media: {
      desktopSrc: '/assets/consulting/pessoas-cultura.webp',
      alt: 'Pessoas, cultura e liderança representadas por uma rede conectada',
      desktopPosition: '67% center',
      mobilePosition: '64% center',
      mobileScale: 1.08,
    },
    see: { label: 'SUL — Recrutamento e Gestão de Pessoas', href: 'https://sul.geniuslead.com.br' },
  },
  {
    name: 'Comercial',
    focus: 'Prospecção · carteira · canais · metas · acompanhamento · conversão',
    text: 'Vendas não podem depender só do dono, da memória do vendedor ou de oportunidades que aparecem. Organizamos a prospecção, cuidamos da carteira e acompanhamos metas para criar um processo comercial consistente, mensurável e capaz de crescer.',
    media: {
      desktopSrc: '/assets/consulting/comercial.webp',
      alt: 'Canais comerciais convergindo para um funil de vendas',
      desktopPosition: '76% center',
      mobilePosition: '72% center',
      mobileScale: 1.1,
    },
    see: { label: 'LESTE — Prospecção, automação e vendas', href: 'https://leste.geniuslead.com.br' },
  },
  {
    name: 'Operação',
    focus: 'Processos · produtividade · capacidade · qualidade · prazo · desperdício',
    text: 'Sua produção vai ter que dar conta de mais volume. Prazo, qualidade e desperdício começam a pressionar a operação. Só contratar mais pessoas não resolve. Melhoramos processos, aplicamos tecnologia e modernizamos a estrutura para ganhar produtividade e capacidade sem perder competitividade.',
    media: {
      desktopSrc: '/assets/consulting/operacao.webp',
      alt: 'Engrenagens e fluxos representando uma operação integrada',
      desktopPosition: '77% center',
      mobilePosition: '72% center',
      mobileScale: 1.12,
    },
  },
  {
    name: 'Infraestrutura',
    focus: 'Sistemas · ferramentas · automação · IA · segurança',
    text: 'Planilhas soltas, sistemas que não conversam e processos manuais podem até funcionar — mas só até a empresa atingir certo tamanho. Colocamos tecnologia para trabalhar a favor da operação: informação centralizada, automação, infraestrutura confiável e ferramentas que realmente resolvem problemas.',
    media: {
      desktopSrc: '/assets/consulting/infraestrutura.webp',
      alt: 'Infraestrutura de tecnologia, servidores e conexões protegidas',
      desktopPosition: '65% center',
      mobilePosition: '62% center',
      mobileScale: 1.1,
    },
    see: { label: 'Produtos & Soluções', href: '#produtos' },
  },
  {
    name: 'Expansão',
    focus: 'Novos mercados · novos produtos · canais · investimentos · projeção internacional',
    text: 'Crescer pode significar uma nova loja, uma fábrica maior, máquinas, novos produtos, canais ou outro país. Planejamos a expansão olhando investimento, capacidade, pessoas e operação para que a empresa cresça sem perder o controle.',
    media: {
      desktopSrc: '/assets/consulting/expansao.webp',
      alt: 'Conexões internacionais representando expansão para novos mercados',
      desktopPosition: '75% center',
      mobilePosition: '70% center',
      mobileScale: 1.1,
    },
    see: { label: 'Bāxī Zhōngguó — Brazil–China Consulting and Business Development', href: 'https://bashizhongguo.com' },
  },
]

export const products: Product[] = [
  {
    name: 'NORTE',
    family: 'Gestão & Operação',
    status: 'ATIVO',
    tagline: 'Indicadores, Gestão e Decisão',
    problem: 'Informações espalhadas, relatórios atrasados e decisões sem uma visão clara da operação.',
    solution: 'Indicadores e informações importantes reunidos em um único painel, atualizados automaticamente.',
    gain: 'Mais velocidade, controle e segurança para decidir.',
    cta: 'Conheça o NORTE →',
    url: 'https://norte.geniuslead.com.br',
    color: '#008FAF',
    media: {
      desktopSrc: '/assets/products/norte.webp',
      alt: 'Painel NORTE reunindo indicadores para gestão e decisão',
      desktopPosition: '62% center',
      mobilePosition: '62% center',
    },
  },
  {
    name: 'SUL',
    family: 'Gestão & Operação',
    status: 'PILOTO / COMERCIAL',
    tagline: 'Recrutamento e Gestão de Pessoas',
    problem: 'Recrutamento, documentos, férias, prazos e pendências de RH espalhados entre pessoas, planilhas e mensagens.',
    solution: 'Uma plataforma para organizar recrutamento, seleção e as principais rotinas de gestão de pessoas.',
    gain: 'Menos pendências. Mais organização e visão clara do RH.',
    cta: 'Conheça o SUL →',
    url: 'https://sul.geniuslead.com.br',
    color: '#008FAF',
    media: {
      desktopSrc: '/assets/products/sul.webp',
      alt: 'Painel SUL aplicado à gestão de pessoas',
      desktopPosition: '68% center',
      mobilePosition: '66% center',
    },
  },
  {
    name: 'LESTE',
    family: 'Gestão & Operação',
    status: 'PILOTO / COMERCIAL',
    tagline: 'Prospecção, automação e vendas',
    problem: 'Leads esfriam, contatos se perdem e o acompanhamento depende demais da memória de cada vendedor.',
    solution: 'CRM com pipeline, automações e agentes para prospecção, contatos e acompanhamento comercial.',
    gain: 'Mais disciplina comercial e menos oportunidades perdidas.',
    cta: 'Conheça o LESTE →',
    url: 'https://leste.geniuslead.com.br',
    color: '#008FAF',
    media: {
      desktopSrc: '/assets/products/leste.webp',
      alt: 'Painel LESTE aplicado à prospecção e gestão comercial',
      desktopPosition: '58% center',
      mobilePosition: '58% center',
    },
  },
  {
    name: 'OESTE',
    family: 'Gestão & Operação',
    status: 'PILOTO / COMERCIAL',
    tagline: 'Despesas, aprovações e controle financeiro',
    problem: 'Compras e despesas chegam ao financeiro sem uma visão clara do que foi solicitado, aprovado e ainda será pago.',
    solution: 'Fluxo de solicitação, cotação, aprovação, pagamento e classificação das despesas.',
    gain: 'Mais controle e capacidade de saber antes o que vai chegar ao caixa.',
    cta: 'Conheça o OESTE →',
    url: 'https://oeste.geniuslead.com.br',
    color: '#008FAF',
    media: {
      desktopSrc: '/assets/products/oeste.webp',
      alt: 'Painel OESTE aplicado à gestão de despesas e aprovações',
      desktopPosition: '62% center',
      mobilePosition: '62% center',
    },
  },
  {
    name: 'GeniusLED',
    family: 'Comunicação & Tecnologia',
    status: 'DISPONÍVEL',
    problem: 'Comunicação visual parada perde impacto e limita campanhas, informações e novas possibilidades de uso do espaço.',
    solution: 'Venda e locação de painéis de LED e soluções de mídia digital.',
    gain: 'Comunicação mais dinâmica, flexível e visível.',
    cta: 'Conheça o GeniusLED →',
    url: 'https://geniusled.com.br',
    color: '#59A000',
    media: {
      desktopSrc: '/assets/products/geniusled.webp',
      alt: 'Soluções GeniusLED integradas a um ambiente comercial',
      desktopPosition: '56% center',
      mobilePosition: '58% center',
    },
  },
  {
    name: 'GeniusLIVE',
    family: 'Comunicação & Tecnologia',
    status: 'DISPONÍVEL',
    problem: 'Instalar um painel é simples. Manter conteúdo, programação e operação atualizados vira mais uma tarefa para a empresa.',
    solution: 'Operação terceirizada de conteúdo e programação para painéis GeniusLED ou de terceiros.',
    gain: 'Painéis sempre ativos sem criar outra rotina interna.',
    cta: 'Conheça o GeniusLIVE →',
    url: 'https://geniuslive.com.br',
    color: '#FF3636',
    media: {
      desktopSrc: '/assets/products/geniuslive.webp',
      alt: 'Central GeniusLIVE operando conteúdos para painéis digitais',
      desktopPosition: '66% center',
      mobilePosition: '66% center',
    },
  },
  {
    name: 'GeniusLAN',
    family: 'Comunicação & Tecnologia',
    status: 'DISPONÍVEL',
    problem: 'Internet instável, Wi-Fi ruim e infraestrutura improvisada afetam praticamente toda a empresa.',
    solution: 'Projeto, implantação e suporte de redes, conectividade e infraestrutura de TI.',
    gain: 'Mais estabilidade, organização e continuidade da operação.',
    cta: 'Conheça o GeniusLAN →',
    url: 'https://geniuslan.com.br',
    color: '#FF751F',
    media: {
      desktopSrc: '/assets/products/geniuslan.webp',
      alt: 'Equipe GeniusLAN implantando infraestrutura de rede',
      desktopPosition: '62% center',
      mobilePosition: '62% center',
    },
  },
  {
    name: 'GeniusLINK',
    family: 'Gestão & Operação',
    status: 'DISPONÍVEL',
    problem: 'Veículos, equipes externas e entregas ficam longe dos olhos da gestão.',
    solution: 'Rastreamento, monitoramento, telemetria, alertas e informações operacionais reunidos em uma única solução.',
    gain: 'Mais controle da operação, segurança e capacidade de auditoria.',
    cta: 'Conheça o GeniusLINK →',
    url: 'https://geniuslink.com.br',
    color: '#00A878',
    media: {
      desktopSrc: '/assets/products/geniuslink.webp',
      alt: 'Central GeniusLINK de monitoramento e rastreamento operacional',
      desktopPosition: '66% center',
      mobilePosition: '66% center',
    },
  },
]

export const cases: CaseStudy[] = [
  {
    name: 'TRANSFORME LOJA',
    eyebrow: '26 meses · Indústria',
    title: 'Estrutura industrial para uma nova escala',
    introduction: 'O crescimento da empresa exigia uma estrutura administrativa e fabril capaz de acompanhar uma operação cada vez maior.',
    structure: [
      'Financeiro e Tesouraria com estrutura de empresa grande.',
      'ERP integrado ao fluxo: importa desenhos, orça e envia para produção.',
      'Comercial, Engenharia e Produção alinhados.',
      'Suprimentos, Estoque e Logística com auditoria e controle.',
      'Indústria 4.0: CAD/CAM e digitalização da engenharia.',
      'Expansão fabril com estudo e aquisição de nova linha CNC.',
    ],
    results: [
      { value: '+63%', label: 'Faturamento 2023 → 2024' },
      { value: '+70%', label: 'Faturamento 2024 → 2025' },
      { value: '+250%', label: 'Capacidade fabril' },
      { value: '+42%', label: 'Quadro de colaboradores' },
    ],
    conclusion: 'A capacidade fabril cresceu muito acima do quadro de funcionários. Processos, métodos e tecnologia passaram a sustentar uma nova escala de operação e uma carteira com clientes de maior porte.',
  },
  {
    name: 'ELISA MIX',
    eyebrow: '22 meses · Varejo',
    title: 'Estrutura para segurar uma rede em expansão acelerada',
    introduction: 'Mais lojas, mais pessoas e mais volume exigiam uma gestão capaz de acompanhar a velocidade do crescimento sem perder controle.',
    structure: [
      'Financeiro, TI, RH, Jurídico e Suprimentos.',
      'Centro de Distribuição.',
      { label: 'NORTE — BI e gestão.', href: 'https://norte.geniuslead.com.br' },
      'ERP customizado.',
      'Estoque e distribuição.',
      'Importação e automação de processos.',
      'Formação contínua dos gerentes.',
      'Governança e compliance.',
    ],
    results: [
      { value: '5 → 8 → 13', label: 'Lojas' },
      { value: '+101%', label: 'Faturamento 2023 → 2024' },
      { value: '+107%', label: 'Faturamento 2024 → 2025' },
      { value: '+21%', label: 'Margem 2024 → 2025' },
    ],
    conclusion: 'A diretoria passou a tomar decisões com dados estruturados, em tempo real e indicadores relevantes, enquanto novas áreas, sistemas e processos davam suporte à expansão da rede.',
  },
  {
    name: 'LEDSIM',
    eyebrow: '9 meses · Indústria & Tecnologia',
    title: 'Primeiro o financeiro. Depois, produtividade e vendas.',
    introduction: 'Colocamos o financeiro sob controle com fluxo de caixa, DRE e redução de custos. A partir daí, avançamos sobre fábrica e comercial, implantando processos e sistemas para ganhar produtividade, margem e previsibilidade.',
    structure: [
      'Fluxo de caixa e DRE.',
      'Controle de custos financeiros.',
      'Replanejamento do layout da fábrica.',
      'Processos produtivos.',
      'Pipeline comercial.',
      'Controle de margem.',
    ],
    results: [
      { value: '+150%', label: 'Produtividade' },
      { value: '↑ Faturamento', label: '' },
      { value: '↓ Custos financeiros', label: '' },
      { value: '↑ Controle de margem', label: '' },
    ],
    conclusion: 'Em nove meses, a empresa ganhou produtividade, organização financeira e maior controle sobre produção, vendas e margem.',
  },
]
